import React, { useEffect } from "react";
import { motion } from "framer-motion";
import { Link, useLocation } from "react-router-dom";
import SmartImage from "@/components/SmartImage";

const MANIFESTO = [
  { n: "01", h: "We built the site by stage, not by feature.", b: "Housing means something different in April than in August. Application deadlines mean something different if you're a junior versus a senior. The five stages, Discover, Plan, Apply, Prepare, Thrive, match the reality of your calendar." },
  { n: "02", h: "Nothing on this site pretends to be a lawyer.", b: "Visa, tax, insurance, we point you toward official sources for anything binding. Everything else is the plain-language map we wish we'd had." },
  { n: "03", h: "We're paid when a student actually lands well.", b: "Concierge packages, honest referral partnerships, no hidden 'lead sales.' If we recommend a school, it's because it fits your profile, not because it pays the highest referral." },
  { n: "04", h: "France is dramatically cheaper, and we say so with real numbers.", b: "Even after the 2026 non-EU tuition change, the four-year math still favours France by tens of thousands of dollars. See the cost breakdown on the homepage." },
];

export default function Discover() {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      // Retry a few times because content mounts + motion animates in.
      const attempt = (n) => {
        const el = document.querySelector(location.hash);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        } else if (n > 0) {
          setTimeout(() => attempt(n - 1), 120);
        }
      };
      setTimeout(() => attempt(6), 120);
    }
  }, [location.hash]);

  return (
    <div className="pt-28 md:pt-32 pb-24 min-h-screen" data-testid="page-discover">
      <div className="max-w-[1200px] mx-auto px-6 md:px-10">
        <div className="flex items-center gap-3 mb-6">
          <span className="rule-gold" /><span className="eyebrow">01 · Discover · Our manifesto</span>
        </div>
        <h1 className="serif text-5xl md:text-7xl leading-[0.95] tracking-tight max-w-4xl">
          Making France feel <em className="italic text-[#b8892b]">like home</em>.
        </h1>
        <p className="mt-6 text-lg text-[#4a5570] max-w-2xl leading-relaxed">
          FrancePath started as a spreadsheet a family passed around at the dinner table. It became a website because
          too many students were being priced out of world-class education for no good reason.
        </p>

        <div className="mt-20 space-y-16">
          {MANIFESTO.map((c, i) => (
            <motion.div key={c.n} initial={{ opacity:0, y:20 }} whileInView={{ opacity:1, y:0 }}
              viewport={{ once:true, margin:"-80px" }} transition={{ duration: 0.6, delay: i * 0.05 }}
              className="grid md:grid-cols-12 gap-6 border-t border-[#0d1b3e]/15 pt-10"
              data-testid={`manifesto-${c.n}`}>
              <div className="md:col-span-3">
                <div className="chapter-num">{c.n}</div>
              </div>
              <div className="md:col-span-9 max-w-3xl">
                <h2 className="serif text-3xl md:text-4xl text-[#0d1b3e] leading-tight tracking-tight">{c.h}</h2>
                <p className="mt-4 text-lg text-[#0d1b3e]/80 leading-relaxed">{c.b}</p>
              </div>
            </motion.div>
          ))}
        </div>

        <div className="mt-20 flex flex-wrap gap-4">
          <Link to="/advisor" className="btn-sharp btn-sharp-gold" data-testid="discover-cta-advisor">Start your free plan</Link>
          <Link to="/plan" className="btn-sharp btn-sharp-outline" data-testid="discover-cta-plan">Browse universities</Link>
        </div>
      </div>

      {/* Founder section — full-bleed */}
      <section id="founder-section" className="mt-32 bg-[#0d1b3e] text-white py-24 md:py-32" data-testid="founder-section">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10">
          <div className="flex items-center gap-3 mb-10">
            <span className="rule-gold" /><span className="eyebrow !text-[#b8892b]">Meet the founder</span>
          </div>

          <div className="grid md:grid-cols-12 gap-10 md:gap-14 items-start">
            {/* Portrait */}
            <motion.div
              initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ duration: 0.8 }}
              className="md:col-span-5"
            >
              <div className="relative aspect-[4/5] overflow-hidden bg-[#0d1b3e] border border-white/10" data-testid="founder-portrait">
                <SmartImage src="/founder/rose.png" alt="Rose Marcelin, founder of FrancePath" label="Rose Marcelin" />
                <div className="absolute top-4 left-4 bg-[#b8892b] text-white text-[10px] uppercase tracking-[0.22em] px-2 py-1">
                  01 · Origine
                </div>
              </div>
              <div className="mt-5">
                <div className="serif text-3xl md:text-4xl leading-tight" data-testid="founder-name">Rose Marcelin</div>
                <div className="mt-2 flex items-center gap-3">
                  <span className="w-6 h-px bg-[#b8892b]" />
                  <span className="text-xs uppercase tracking-[0.24em] text-white/60">Founder of FrancePath</span>
                </div>
                <a
                  href="https://www.linkedin.com/in/rose-clervil/"
                  target="_blank"
                  rel="noreferrer"
                  data-testid="founder-linkedin"
                  className="mt-4 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white/70 hover:text-[#b8892b] transition-colors"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path d="M20.45 20.45h-3.55v-5.57c0-1.33-.02-3.03-1.85-3.03-1.85 0-2.13 1.44-2.13 2.93v5.67H9.37V9h3.4v1.56h.05c.47-.89 1.63-1.85 3.35-1.85 3.58 0 4.24 2.36 4.24 5.42v6.32zM5.34 7.44a2.06 2.06 0 1 1 0-4.12 2.06 2.06 0 0 1 0 4.12zM7.11 20.45H3.56V9h3.55v11.45zM22.23 0H1.77C.79 0 0 .77 0 1.72v20.56C0 23.23.79 24 1.77 24h20.46c.98 0 1.77-.77 1.77-1.72V1.72C24 .77 23.21 0 22.23 0z"/>
                  </svg>
                  <span>LinkedIn · Rose Clervil</span>
                </a>
              </div>
            </motion.div>

            {/* Story */}
            <div className="md:col-span-7 space-y-8">
              <motion.h2
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ duration: 0.7 }}
                className="serif text-4xl md:text-6xl leading-[0.98] tracking-tight"
              >
                Born in France. <br />
                <em className="italic text-[#b8892b] serif">Raised between two countries.</em>
              </motion.h2>

              <motion.div
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ duration: 0.7, delay: 0.05 }}
                className="space-y-6 text-lg text-white/85 leading-relaxed max-w-2xl"
                data-testid="founder-bio"
              >
                <p className="serif italic text-2xl md:text-3xl text-white leading-snug">
                  "Moving between countries taught me something early: the hardest part is not always the move itself. It is knowing where to start."
                </p>
                <p>
                  At 11 years old, I moved from France to Florida, where I attended middle school and high school. I learned how to adapt to a new culture, a new education system, and a completely different way of life.
                </p>
                <p>
                  Years later, when I returned to France, I expected everything to feel familiar. Instead, I found myself navigating France almost like an international student. Understanding universities, administrative steps, housing, paperwork, and everyday life came with challenges I never expected.
                </p>
                <p className="text-[#b8892b] serif italic text-xl">
                  That experience became the foundation of FrancePath.
                </p>
                <p>
                  I created this platform because I believe studying in France should be an exciting opportunity, not a stressful maze of unanswered questions. Students and families deserve clear guidance, reliable information, and someone who understands both sides of the journey.
                </p>
                <p>
                  FrancePath helps American students discover their options, make confident decisions, complete the necessary steps, and arrive in France ready to thrive.
                </p>
                <p className="text-white">
                  Because I know what it feels like to start over somewhere new. And I know how much easier it is when someone helps you find your way.
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ duration: 0.7, delay: 0.1 }}
                className="grid sm:grid-cols-3 gap-6 border-t border-white/10 pt-8"
              >
                <div>
                  <div className="chapter-num !text-[#b8892b]">01</div>
                  <div className="serif text-xl mt-3 leading-tight">France, age 0 to 11</div>
                  <p className="text-sm text-white/60 mt-2 leading-relaxed">Native French. The paperwork, the culture, the everyday rhythms are second nature.</p>
                </div>
                <div>
                  <div className="chapter-num !text-[#b8892b]">02</div>
                  <div className="serif text-xl mt-3 leading-tight">Florida, age 11 to 18</div>
                  <p className="text-sm text-white/60 mt-2 leading-relaxed">Middle school and high school in the US. Learned the American college race from the inside.</p>
                </div>
                <div>
                  <div className="chapter-num !text-[#b8892b]">03</div>
                  <div className="serif text-xl mt-3 leading-tight">Back to France, on both sides</div>
                  <p className="text-sm text-white/60 mt-2 leading-relaxed">Rediscovered France almost as an international student. FrancePath is the guide she wished she'd had.</p>
                </div>
              </motion.div>

              <div className="flex flex-wrap gap-3 pt-2">
                <a href="https://wa.me/33618797689" target="_blank" rel="noreferrer" className="btn-sharp btn-sharp-gold" data-testid="founder-whatsapp">Ask Rose anything</a>
                <a href="mailto:francepathfr@gmail.com" className="btn-sharp !bg-transparent !border-white/40 !text-white hover:!bg-white hover:!text-[#0d1b3e]" data-testid="founder-email">Email Rose</a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
